import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  getLinerConfig,
  getLocationsByCountry,
  getRbacDetails,
  getUserGridsDefinition,
  getUserProfile,
} from "../actions/userDetails";

interface Profile {
  userId: string | number | null;
  partnerId: string | number | null;
  username: string | null;
  partnerName: string | null;
  email: string | null;
  partnerType: string | null;
  partnerCountry: string | null;
  pwdExpired: string | null;
  isTNCAccepted: string | null;
}

interface RBACDetails {
  gridPrivate: string;
  gridShared: string;
  gridSystem: string;
  blDashboard: string;
  invDashboard: string;
  createUpdatePayment: string;
  enableHelpDocumentAddition:string;
  showHelpDocumentDashboard: string;
  showManualInvoice: string;
  showNewBlRequest: string;
  showAddCustomerDetails: string;
  paymentDetailsCheckboxAccess: string;
  createUpdateCredit: string;
  useCredit: string;
  nominactionCUD: string;
  deliveryCUD: string;
  videoPaypalPopup: string;
  cfsEmptyName: string;
  doExtCfsEmptyName: string;
  overStayLink: string;

  deliveryView: string;
  createMergePayment: string;
  deliveryApproveReject: string;
  invoiceChangeCUD: string;
  exportInvoiceChangeCUD: string;
  bulkPaymentDashboard: string;
  invoiceChangeApproveReject: string;
  exportInvoiceChangeApproveReject: string;
  invoiceChangeView: string;
  demurrageCUD: string;
  doExtCUD: string;
  refundRequestCUD: string;
  invoiceRequestCUD: string;
  demurrageView: string;
  demurrageApproveReject: string;
  doExtApproveReject: string;
  containerDropOff: string;
  containerDropOffApproveReject: string;
  refundApproveReject: string;
  invoiceRequestApproveReject: string;
  doDashboard: string;
  customerMgmtCreate: string;
  customerMgmtView: string;
  customerMgmtApproveReject: string;
  rejectionLevelSelection: string;
  doExtRejectionLevelSelection: string;
  userMgmtCreate: string;
  customerCodeReq: string;
  showEmailTemplateDashboard: string;
  userMgmtView: string;
  userMgmtApproveReject: string;
  returnCntCUD: string;
  returnCntView: string;
  returnCntApproveReject: string;
  registrationMstDashboard: string;
  pickUpApproveReject: string;
  pickUpCUD: string;
  instructionDashboard: string;
  agentCreate: string;
  paymentDashboard: string;
  multiInstructCreate: string;
  agentNominationApproveReject: string;
  paymentApproveReject: string;
  linerPaymentModeBankEdit: string;
  customerBeneficiaryAccountEdit: string;
  depositSlipDownload: string;
  standingInstDashboard: string;
  standaloneInvPymtCUD: string;
  truckMasterDashboard: string;
  getInvoiceCUD: string;
  bulkPymtCUD: string;
  paymentProofAttachment: string;
  paymentProofRequired: string;
  payDetailsDashboard: string;
  pdfParser: string;
  reportDashboard: string;
  allowInstructionAssignment: string;
  crrUnpaidHardStop: string;
  crrUnpaidSoftStop: string;
  showPaymentTransaction: string;
  showAdminRefNo: string;
  crrPopAttStop: string;
  saveAsDraft: string;
  doExtSaveAsDraft: string;
  refundSaveAsDraft: string;
  showMarkasRefundedButton: string;
  snoozeCUD: string;
  showPlatformFees: string;
  overStayApproveReject: string;
  customerCodeApproveReject: string;
  submitCustomerCodeReq: string;
  crrLevelOne: string;
  crrLevelTwo: string;
  crrLevelThree: string;
  crrLevelFour: string;
  crrLevelFive: string;
  crrLevelSix: string;
  crrLevelSeven: string;
  crrLevelEight: string;
  crrLevelNine: string;
  crrLevelTen: string;
  doExtLevelOne: string;
  doExtLevelTwo: string;
  doExtLevelThree: string;
  doExtLevelFour: string;
  doExtLevelFive: string;
  doExtLevelSix: string;
  doExtLevelSeven: string;
  doExtLevelEight: string;
  doExtLevelNine: string;
  doExtLevelTen: string;
  refundLevelOne: string;
  refundLevelTwo: string;
  refundLevelThree: string;
  refundLevelFour: string;
  refundLevelFive: string;
  refundLevelSix: string;
  refundLevelSeven: string;
  refundLevelEight: string;
  refundLevelNine: string;
  refundLevelTen: string;
  receiptDashboard: string;
  gateInGateOutDashboard: string;
  mappingDashboard: string;
  sezDashboard: string;
  sezInvoiceUpload: string;
  sezFieldEdit: string;
  emailDashboard: string;
  payorDashboard: string;
  sendPaymentToLiner: string;
  showEblColumn: string;
  revisitPayment: string;
  paymentDashboardRemarks: string;
  paymentApproveRemarkMandate: string;
  showPmtModifiedBy: string;
  showPmtModifiedTimestamp: string;
  showPmtAdminRef: string;
  odexAdmin: string;
}

interface RbacDetailsList {
  list: RBACDetails[];
  next: string | null;
  previous: string | null;
}

interface IUserGridDefinition {
  //columnConfig: any[];
  columnConfigGrid: string;
  gridId: number;
  gridName: string | null;
  linerId: number;
}

interface UserDetails {
  profile: Profile;
  userGridDefinitions: IUserGridDefinition[];
  rbacDetails: RBACDetails;
  data: RbacDetailsList;
  linerDataCountry: string;
  selectedPort: string;
  countryList: any;
  locationList: Array<{ locationCode: string, locationName: string }>;
  linerGrpCountry: string;
  selectedLinerId: string;
  linerCountryConfig: any;
}

const initialState: UserDetails = {
  profile: {
    userId: null,
    partnerId: null,
    username: null,
    partnerName: null,
    email: null,
    partnerType: null,
    partnerCountry: null,
    pwdExpired: null,
    isTNCAccepted: null,
  },
  userGridDefinitions: [],
  data: {
    list: [],
    next: null,
    previous: null,
  },
  rbacDetails: {
    cfsEmptyName: "",
    doExtCfsEmptyName: "",
    bulkPaymentDashboard: "",
    gridPrivate: "",
    videoPaypalPopup: "",
    gridShared: "",
    gridSystem: "",
    blDashboard: "",
    doDashboard: "",
    pdfParser: "",
    invDashboard: "",
    createUpdatePayment: "",
    enableHelpDocumentAddition:"",
    showHelpDocumentDashboard: "",
    showManualInvoice: "",
    showNewBlRequest: "",
    showAddCustomerDetails: "",
    createMergePayment: "",
    paymentDetailsCheckboxAccess: "",
    truckMasterDashboard: "",
    createUpdateCredit: "",
    useCredit: "",
    nominactionCUD: "",
    deliveryCUD: "",
    invoiceChangeCUD: "",
    exportInvoiceChangeCUD: "",
    bulkPymtCUD: "",
    invoiceChangeApproveReject: "",
    exportInvoiceChangeApproveReject: "",
    registrationMstDashboard: "",
    paymentApproveReject: "",
    linerPaymentModeBankEdit: "",
    customerBeneficiaryAccountEdit: "",
    depositSlipDownload: "",
    invoiceChangeView: "",
    deliveryView: "",
    deliveryApproveReject: "",
    demurrageCUD: "",
    doExtCUD: "",
    refundRequestCUD: "",
    invoiceRequestCUD: "",
    demurrageView: "",
    demurrageApproveReject: "",
    doExtApproveReject: "",
    containerDropOff: "",
    containerDropOffApproveReject: "",
    refundApproveReject: "",
    invoiceRequestApproveReject: "",
    customerMgmtCreate: "",
    customerMgmtView: "",
    customerMgmtApproveReject: "",
    rejectionLevelSelection: "",
    doExtRejectionLevelSelection: "",
    userMgmtCreate: "",
    customerCodeReq: "",
    showEmailTemplateDashboard: "",
    userMgmtView: "",
    userMgmtApproveReject: "",
    returnCntCUD: "",
    returnCntView: "",
    returnCntApproveReject: "",
    pickUpCUD: "",
    pickUpApproveReject: "",
    instructionDashboard: "",
    agentCreate: "",
    paymentDashboard: "",
    multiInstructCreate: "",
    agentNominationApproveReject: "",
    standingInstDashboard: "",
    standaloneInvPymtCUD: "",
    getInvoiceCUD: "",
    paymentProofAttachment: "",
    paymentProofRequired: "",
    payDetailsDashboard: "",
    reportDashboard: "",
    allowInstructionAssignment: "",
    crrUnpaidHardStop: "",
    crrUnpaidSoftStop: "",
    showPaymentTransaction: "",
    showAdminRefNo: "",
    crrPopAttStop: "",
    saveAsDraft: "",
    doExtSaveAsDraft: "",
    refundSaveAsDraft: "",
    showMarkasRefundedButton: "",
    snoozeCUD: "",
    overStayApproveReject: "",
    customerCodeApproveReject: "",
    submitCustomerCodeReq: "",
    showPlatformFees: "",
    crrLevelOne: "",
    crrLevelTwo: "",
    crrLevelThree: "",
    crrLevelFour: "",
    crrLevelFive: "",
    crrLevelSix: "",
    crrLevelSeven: "",
    crrLevelEight: "",
    crrLevelNine: "",
    crrLevelTen: "",
    doExtLevelOne: "",
    doExtLevelTwo: "",
    doExtLevelThree: "",
    doExtLevelFour: "",
    doExtLevelFive: "",
    doExtLevelSix: "",
    doExtLevelSeven: "",
    doExtLevelEight: "",
    doExtLevelNine: "",
    doExtLevelTen: "",
    refundLevelOne: "",
    refundLevelTwo: "",
    refundLevelThree: "",
    refundLevelFour: "",
    refundLevelFive: "",
    refundLevelSix: "",
    refundLevelSeven: "",
    refundLevelEight: "",
    refundLevelNine: "",
    refundLevelTen: "",
    receiptDashboard: "",
    gateInGateOutDashboard: "",
    mappingDashboard: "",
    sezDashboard: "",
    sezInvoiceUpload: "",
    sezFieldEdit: "",
    emailDashboard: "",
    payorDashboard:"",
    sendPaymentToLiner: "",
    showEblColumn: "",
    revisitPayment: "",
    paymentDashboardRemarks: "",
    paymentApproveRemarkMandate: "",
    showPmtModifiedBy: "",
    showPmtModifiedTimestamp: "",
    showPmtAdminRef: "",
    overStayLink: "",
    odexAdmin: ""
  },
  linerDataCountry: "",
  selectedPort: "",
  countryList: [],
  locationList: [],
  linerGrpCountry: "",
  selectedLinerId: "1",
  linerCountryConfig: {}
};

export const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {
    updateProfile: (state, action: PayloadAction<Profile>) => {
      state.profile = action.payload;
    },
    updateRbacDetails: (state, action: PayloadAction<any>) => {
      state.rbacDetails.gridPrivate = action.payload.gridPrivate;
      state.rbacDetails.gridShared = action.payload.gridShared;
      state.rbacDetails.gridSystem = action.payload.gridSystem;
      state.rbacDetails.blDashboard = action.payload.blDashboard;
      state.rbacDetails.invDashboard = action.payload.invDashboard;
      state.rbacDetails.doDashboard = action.payload.doDashboard;
      state.rbacDetails.createUpdatePayment =
        action.payload.createUpdatePayment;
        state.rbacDetails.enableHelpDocumentAddition =
        action.payload.enableHelpDocumentAddition;
        state.rbacDetails.showHelpDocumentDashboard =
        action.payload.showHelpDocumentDashboard;
      state.rbacDetails.showManualInvoice = action.payload.showManualInvoice;
      state.rbacDetails.showNewBlRequest = action.payload.showNewBlRequest;
      state.rbacDetails.showAddCustomerDetails = action.payload.showAddCustomerDetails;
      state.rbacDetails.createMergePayment = action.payload.createMergePayment;
      state.rbacDetails.paymentDetailsCheckboxAccess =
        action.payload.paymentDetailsCheckboxAccess;
      state.rbacDetails.createUpdateCredit = action.payload.createUpdateCredit;
      state.rbacDetails.useCredit = action.payload.useCredit;
      state.rbacDetails.nominactionCUD = action.payload.nominactionCUD;
      state.rbacDetails.deliveryCUD = action.payload.deliveryCUD;
      state.rbacDetails.invoiceChangeCUD = action.payload.invoiceChangeCUD;
      state.rbacDetails.exportInvoiceChangeCUD = action.payload.exportInvoiceChangeCUD;
      state.rbacDetails.bulkPaymentDashboard = action.payload.bulkPaymentDashboard;
      state.rbacDetails.truckMasterDashboard = action.payload.truckMasterDashboard;

      state.rbacDetails.invoiceChangeApproveReject =
        action.payload.invoiceChangeApproveReject;
      state.rbacDetails.exportInvoiceChangeApproveReject =
        action.payload.exportInvoiceChangeApproveReject;
      state.rbacDetails.invoiceChangeView = action.payload.invoiceChangeView;
      state.rbacDetails.deliveryView = action.payload.deliveryView;
      state.rbacDetails.deliveryApproveReject =
        action.payload.deliveryApproveReject;
      state.rbacDetails.demurrageCUD = action.payload.demurrageCUD;
      state.rbacDetails.doExtCUD = action.payload.doExtCUD;
      state.rbacDetails.refundRequestCUD = action.payload.refundRequestCUD;
      state.rbacDetails.invoiceRequestCUD = action.payload.invoiceRequestCUD || "";
      state.rbacDetails.demurrageView = action.payload.demurrageView;
      state.rbacDetails.demurrageApproveReject =
        action.payload.demurrageApproveReject;
      state.rbacDetails.doExtApproveReject =
        action.payload.doExtApproveReject;
      state.rbacDetails.containerDropOff =
        action.payload.containerDropOff || "";
      state.rbacDetails.containerDropOffApproveReject =
        action.payload.containerDropOffApproveReject || "";
      state.rbacDetails.refundApproveReject =
        action.payload.refundApproveReject;
      state.rbacDetails.invoiceRequestApproveReject =
        action.payload.invoiceRequestApproveReject || "";
      state.rbacDetails.customerMgmtCreate = action.payload.customerMgmtCreate;
      state.rbacDetails.customerMgmtView = action.payload.customerMgmtView;
      state.rbacDetails.customerMgmtApproveReject =
        action.payload.customerMgmtApproveReject;
      state.rbacDetails.rejectionLevelSelection =
        action.payload.rejectionLevelSelection;
      state.rbacDetails.doExtRejectionLevelSelection =
        action.payload.doExtRejectionLevelSelection;
      state.rbacDetails.userMgmtCreate = action.payload.userMgmtCreate;
      state.rbacDetails.customerCodeReq = action.payload.customerCodeReq;
      state.rbacDetails.showEmailTemplateDashboard = action.payload.showEmailTemplateDashboard;
      state.rbacDetails.userMgmtView = action.payload.userMgmtView;
      state.rbacDetails.userMgmtApproveReject =
        action.payload.userMgmtApproveReject;
      state.rbacDetails.returnCntCUD = action.payload.returnCntCUD;
      state.rbacDetails.returnCntView = action.payload.returnCntView;
      state.rbacDetails.returnCntApproveReject =
        action.payload.returnCntApproveReject;
      state.rbacDetails.pickUpCUD = action.payload.pickUpCUD;
      state.rbacDetails.pickUpApproveReject =
        action.payload.pickUpApproveReject;
      state.rbacDetails.instructionDashboard =
        action.payload.instructionDashboard;
      state.rbacDetails.registrationMstDashboard =
        action.payload.registrationMstDashboard;
      state.rbacDetails.agentCreate = action?.payload?.agentCreate;
      state.rbacDetails.paymentDashboard = action?.payload?.paymentDashboard;
      state.rbacDetails.multiInstructCreate =
        action?.payload?.multiInstructCreate;
      state.rbacDetails.agentNominationApproveReject =
        action?.payload?.agentNominationApproveReject || "";
      state.rbacDetails.paymentApproveReject =
        action?.payload?.paymentApproveReject || "";
      state.rbacDetails.linerPaymentModeBankEdit =
        action?.payload?.linerPaymentModeBankEdit || "";
      state.rbacDetails.customerBeneficiaryAccountEdit =
        action?.payload?.customerBeneficiaryAccountEdit || "";
      state.rbacDetails.depositSlipDownload =
        action?.payload?.depositSlipDownload || "";
      state.rbacDetails.standingInstDashboard =
        action?.payload?.standingInstDashboard;
      state.rbacDetails.standaloneInvPymtCUD =
        action?.payload?.standaloneInvPymtCUD || "";
      state.rbacDetails.getInvoiceCUD = action?.payload?.getInvoiceCUD || "";
      state.rbacDetails.bulkPymtCUD = action?.payload?.bulkPymtCUD || "";
      state.rbacDetails.paymentProofAttachment =
        action.payload.paymentProofAttachment;
      state.rbacDetails.paymentProofRequired =
        action.payload.paymentProofRequired;
      state.rbacDetails.payDetailsDashboard =
        action.payload.payDetailsDashboard || "";
      state.rbacDetails.reportDashboard = action.payload.reportDashboard || "";
      state.rbacDetails.allowInstructionAssignment =
        action.payload.allowInstructionAssignment || "";
      state.rbacDetails.crrUnpaidHardStop =
        action.payload.crrUnpaidHardStop || "";
      state.rbacDetails.crrUnpaidSoftStop =
        action.payload.crrUnpaidSoftStop || "";
      state.rbacDetails.showPaymentTransaction =
        action.payload.showPaymentTransaction || "";
      state.rbacDetails.showAdminRefNo = action.payload.showAdminRefNo || "";
      state.rbacDetails.saveAsDraft = action.payload.saveAsDraft || "";
      state.rbacDetails.doExtSaveAsDraft = action.payload.doExtSaveAsDraft || "";
      state.rbacDetails.refundSaveAsDraft = action.payload.refundSaveAsDraft || "";
      state.rbacDetails.showMarkasRefundedButton =
        action.payload.showMarkasRefundedButton || "";
      state.rbacDetails.snoozeCUD = action.payload.snoozeCUD || "";
      state.rbacDetails.overStayApproveReject =
        action.payload.overStayApproveReject || "";
      state.rbacDetails.customerCodeApproveReject =
        action.payload.customerCodeApproveReject || "";
      state.rbacDetails.submitCustomerCodeReq =
        action.payload.submitCustomerCodeReq || "";
      state.rbacDetails.showPlatformFees =
        action.payload.showPlatformFees || "";
      state.rbacDetails.crrLevelOne = action.payload.crrLevelOne || "";
      state.rbacDetails.crrLevelTwo = action.payload.crrLevelTwo || "";
      state.rbacDetails.crrLevelThree = action.payload.crrLevelThree || "";
      state.rbacDetails.crrLevelFour = action.payload.crrLevelFour || "";
      state.rbacDetails.crrLevelFive = action.payload.crrLevelFive || "";
      state.rbacDetails.crrLevelSix = action.payload.crrLevelSix || "";
      state.rbacDetails.crrLevelSeven = action.payload.crrLevelSeven || "";
      state.rbacDetails.crrLevelEight = action.payload.crrLevelEight || "";
      state.rbacDetails.crrLevelNine = action.payload.crrLevelNine || "";
      state.rbacDetails.crrLevelTen = action.payload.crrLevelTen || "";
      state.rbacDetails.doExtLevelOne = action.payload.doExtLevelOne || "";
      state.rbacDetails.doExtLevelTwo = action.payload.doExtLevelTwo || "";
      state.rbacDetails.doExtLevelThree = action.payload.doExtLevelThree || "";
      state.rbacDetails.doExtLevelFour = action.payload.doExtLevelFour || "";
      state.rbacDetails.doExtLevelFive = action.payload.doExtLevelFive || "";
      state.rbacDetails.doExtLevelSix = action.payload.doExtLevelSix || "";
      state.rbacDetails.doExtLevelSeven = action.payload.doExtLevelSeven || "";
      state.rbacDetails.doExtLevelEight = action.payload.doExtLevelEight || "";
      state.rbacDetails.doExtLevelNine = action.payload.doExtLevelNine || "";
      state.rbacDetails.doExtLevelTen = action.payload.doExtLevelTen || "";
      state.rbacDetails.refundLevelOne = action.payload.refundLevelOne || "";
      state.rbacDetails.refundLevelTwo = action.payload.refundLevelTwo || "";
      state.rbacDetails.refundLevelThree = action.payload.refundLevelThree || "";
      state.rbacDetails.refundLevelFour = action.payload.refundLevelFour || "";
      state.rbacDetails.refundLevelFive = action.payload.refundLevelFive || "";
      state.rbacDetails.refundLevelSix = action.payload.refundLevelSix || "";
      state.rbacDetails.refundLevelSeven = action.payload.refundLevelSeven || "";
      state.rbacDetails.refundLevelEight = action.payload.refundLevelEight || "";
      state.rbacDetails.refundLevelNine = action.payload.refundLevelNine || "";
      state.rbacDetails.refundLevelTen = action.payload.refundLevelTen || "";
      state.rbacDetails.receiptDashboard =
        action.payload.receiptDashboard || "";
      state.rbacDetails.gateInGateOutDashboard =
        action.payload.gateInGateOutDashboard || "";
      state.rbacDetails.mappingDashboard = action.payload.mappingDashboard;
      state.rbacDetails.sezDashboard =
        action.payload.sezDashboard ||
        action.payload.SezDashboard ||
        "";
      state.rbacDetails.sezInvoiceUpload =
        action.payload.sezInvoiceUpload ||
        action.payload.SezInvoiceUpload ||
        "";
      state.rbacDetails.sezFieldEdit =
        action.payload.sezFieldEdit ||
        action.payload.SezFieldEdit ||
        "";
      state.rbacDetails.videoPaypalPopup =
        action.payload.videoPaypalPopup || "";
      state.rbacDetails.crrPopAttStop = action.payload.crrPopAttStop;
      state.rbacDetails.emailDashboard = action.payload.emailDashboard || "";
      state.rbacDetails.payorDashboard = action.payload.payorDashboard || "";

      state.rbacDetails.revisitPayment = action.payload.revisitPayment || "";
      state.rbacDetails.paymentDashboardRemarks = action.payload.paymentDashboardRemarks || "";
      state.rbacDetails.sendPaymentToLiner =
        action.payload.sendPaymentToLiner || "";
      state.rbacDetails.showEblColumn =
        action.payload.showEblColumn || "";
      state.rbacDetails.pdfParser =
        action.payload.pdfParser || "";
      state.rbacDetails.paymentApproveRemarkMandate =
        action.payload.paymentApproveRemarkMandate || "";
      state.rbacDetails.showPmtModifiedBy =
        action.payload.showPmtModifiedBy || "";
      state.rbacDetails.showPmtModifiedTimestamp =
        action.payload.showPmtModifiedTimestamp || "";
      state.rbacDetails.showPmtAdminRef =
        action.payload.showPmtAdminRef || "";
      state.rbacDetails.odexAdmin =
        action.payload.odexAdmin || "";
      state.rbacDetails.cfsEmptyName = action.payload.cfsEmptyName ?? "";
      state.rbacDetails.doExtCfsEmptyName = action.payload.doExtCfsEmptyName ?? "";
      state.rbacDetails.overStayLink = action.payload.overStayLink ?? "";
    },
    updateLinerDataCountry: (state, action: PayloadAction<any>) => {
      state.linerDataCountry = action.payload;
    },

    updateLinerCountryList: (state, action: PayloadAction<any>) => {
      state.countryList = action.payload;
    },
    updateLocationList: (state, action: PayloadAction<any>) => {
      state.locationList = action.payload;
    },
    updatelinerGrpCountry: (state, action: PayloadAction<any>) => {
      state.linerGrpCountry = action.payload;
    },
    updateSelectedPort: (state, action: PayloadAction<any>) => {
      state.selectedPort = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getUserProfile.pending, (state, action: any) => {
      //  state.dataLoading = true;
      //  state.profile = action?.payload?.data || {} ;
    });
    builder.addCase(getUserProfile.fulfilled, (state, action: any) => {
      //   state.dataLoading = false;

      state.profile = action?.payload?.data || {};
    });
    builder.addCase(getUserProfile.rejected, (state, action) => {
      // state.dataLoading = false;
    });
    builder.addCase(getLocationsByCountry.fulfilled, (state, action: any) => {
      console.log("action.payload", action)
      state.locationList = action.payload;
    });
    builder.addCase(getUserGridsDefinition.fulfilled, (state, action) => {
      state.userGridDefinitions = action.payload;
    });
    builder.addCase(getRbacDetails.rejected, (state, action) => {
    });
    builder.addCase(getRbacDetails.fulfilled, (state, action: any) => {
      state.data = action.payload;
    });
    builder.addCase(getLinerConfig.fulfilled, (state, action) => {
      state.linerCountryConfig = action.payload
    })
  },
});

export const {
  updateProfile,
  updateRbacDetails,
  updateLinerDataCountry,
  updateLinerCountryList,
  updateLocationList,
  updatelinerGrpCountry,
  updateSelectedPort,
} = userDetailsSlice.actions;

export default userDetailsSlice.reducer;
